import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { PageHeader } from '@/components/common';
import complianceService, { AdCreative, ComplianceCheckResponse } from '@/services/compliance.service';

const CompliancePage = () => {
  const [formData, setFormData] = useState<AdCreative>({
    imageUrl: '',
    primaryText: '',
    headline: '',
    articleUrl: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ComplianceCheckResponse | null>(null);
  const [viewMode, setViewMode] = useState<'report' | 'json'>('report');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.imageUrl.trim()) {
      errors.imageUrl = 'Image URL is required';
    } else if (!isValidUrl(formData.imageUrl)) {
      errors.imageUrl = 'Please enter a valid URL';
    }

    if (!formData.primaryText.trim()) {
      errors.primaryText = 'Primary text is required';
    }

    if (!formData.headline.trim()) {
      errors.headline = 'Headline is required';
    }

    if (!formData.articleUrl.trim()) {
      errors.articleUrl = 'Article URL is required';
    } else if (!isValidUrl(formData.articleUrl)) {
      errors.articleUrl = 'Please enter a valid URL';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await complianceService.checkCompliance([formData]);
      setResult(response);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to check compliance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AdCreative, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const renderReport = () => {
    if (!result || !result.ads || result.ads.length === 0) {
      return (
        <Typography color="text.secondary" align="center">
          No results available
        </Typography>
      );
    }

    const ad = result.ads[0];
    const isCompliant = ad.compliant === true;

    return (
      <Box>
        {/* Status Badge */}
        <Chip
          icon={isCompliant ? <CheckCircleIcon /> : <CancelIcon />}
          label={isCompliant ? 'COMPLIANT' : 'NOT COMPLIANT'}
          color={isCompliant ? 'success' : 'error'}
          sx={{ mb: 3, fontWeight: 'bold', fontSize: '14px', height: 36 }}
        />

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Total Issues
              </Typography>
              <Typography variant="h4" fontWeight={700} color="text.primary">
                {ad.issueCount || 0}
              </Typography>
            </CardContent>
          </Card>
          {ad.issueTypes && (
            <>
              <Card sx={{ flex: 1, minWidth: 150 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Creative Issues
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="text.primary">
                    {ad.issueTypes.creative || 0}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 150 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Article Issues
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="text.primary">
                    {ad.issueTypes.article || 0}
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}
        </Box>

        {/* Issues */}
        {ad.issues && ad.issues.length > 0 ? (
          <Box>
            <Typography variant="h6" color="error.main" sx={{ mb: 2 }}>
              Issues Found:
            </Typography>
            {ad.issues.map((issue, index) => (
              <Card
                key={index}
                sx={{
                  mb: 2,
                  borderLeft: 4,
                  borderColor: 'error.main',
                  backgroundColor: 'error.lighter',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningIcon color="error" />
                    <Typography variant="subtitle1" fontWeight={600} color="error.main">
                      {issue.key ? issue.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : `Issue #${index + 1}`}
                    </Typography>
                  </Box>

                  {issue.reason && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        Reason:
                      </Typography>
                      <Typography variant="body2">{issue.reason}</Typography>
                    </Box>
                  )}

                  {issue.tip && (
                    <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Tip:
                      </Typography>
                      <Typography variant="body2">{issue.tip}</Typography>
                    </Alert>
                  )}

                  {issue.offendingText && issue.offendingText.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <FlagIcon fontSize="small" />
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          Offending Text:
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {issue.offendingText.map((text, i) => (
                          <Chip
                            key={i}
                            label={text}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {issue.where && issue.where.length > 0 && (
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        Found in:
                      </Typography>
                      <Typography variant="body2">{issue.where.join(', ')}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : isCompliant ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            No compliance issues found. Your ad looks good!
          </Alert>
        ) : null}
      </Box>
    );
  };

  const renderJson = () => {
    if (!result) {
      return (
        <Typography color="text.secondary" align="center">
          No data available
        </Typography>
      );
    }

    return (
      <Box
        component="pre"
        sx={{
          backgroundColor: '#1F2937',
          color: '#10B981',
          fontFamily: 'monospace',
          fontSize: '13px',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 600,
        }}
      >
        {JSON.stringify(result, null, 2)}
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <PageHeader
        title="Ad Compliance Check"
        subtitle="Check your ad creative compliance using the Compliance Manager API"
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>About:</strong> Fill in the form below with your ad details and click "Check Compliance" to validate
        compliance.
      </Alert>

      {/* Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ad Information
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Image URL"
              placeholder="https://img.rok-dashboard.com/o1.jpg"
              value={formData.imageUrl}
              onChange={(e) => handleInputChange('imageUrl', e.target.value)}
              error={!!formErrors.imageUrl}
              helperText={formErrors.imageUrl}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Primary Text"
              placeholder="Enter your ad primary text here..."
              value={formData.primaryText}
              onChange={(e) => handleInputChange('primaryText', e.target.value)}
              error={!!formErrors.primaryText}
              helperText={formErrors.primaryText}
              required
            />

            <TextField
              fullWidth
              label="Headline"
              placeholder="Enter your ad headline"
              value={formData.headline}
              onChange={(e) => handleInputChange('headline', e.target.value)}
              error={!!formErrors.headline}
              helperText={formErrors.headline}
              required
            />

            <TextField
              fullWidth
              label="Article URL"
              placeholder="https://results.ageful.com/article-url/"
              value={formData.articleUrl}
              onChange={(e) => handleInputChange('articleUrl', e.target.value)}
              error={!!formErrors.articleUrl}
              helperText={formErrors.articleUrl}
              required
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
              disabled={loading}
              sx={{ alignSelf: 'center', minWidth: 200 }}
            >
              {loading ? 'Checking...' : 'Check Compliance'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {(result || loading) && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Output:</Typography>
              {result && (
                <ToggleButtonGroup
                  size="small"
                  value={viewMode}
                  exclusive
                  onChange={(_, value) => value && setViewMode(value)}
                >
                  <ToggleButton value="report">
                    <DescriptionIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Report
                  </ToggleButton>
                  <ToggleButton value="json">
                    <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
                    JSON
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography color="text.secondary">Checking compliance...</Typography>
              </Box>
            ) : (
              <Box>{viewMode === 'report' ? renderReport() : renderJson()}</Box>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default CompliancePage;
